import React, { useEffect, useState } from 'react'
import { Tournament } from '../types'
import { apiGet } from '../utils/api'

interface Props {
  tournament: Tournament
  onClose: () => void
}

const fmtMoney = (v: any) => {
  const n = Number(v)
  if (!isFinite(n)) return '0,00'
  try { return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) } catch { return n.toFixed(2).replace('.', ',') }
}

const TournamentBudgetComparisonModal: React.FC<Props> = ({ tournament, onClose }) => {
  const [detail, setDetail] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        // Получаем последний snapshot
        let snapshotId: string | null = null
        let snapshotPayload: any | null = null
        try {
          const list = await apiGet<any[]>(`/tournaments/${(tournament as any)?.id}/budget-snapshots`)
          if (Array.isArray(list) && list.length > 0) {
            snapshotId = list[0].id
            // Подстрахуемся и достанем payload, чтобы передать в заголовке
            try {
              const snap = await apiGet<any>(`/tournaments/budget-snapshots/${snapshotId}`)
              snapshotPayload = snap?.payloadParsed || (snap?.payload ? JSON.parse(snap.payload) : null)
            } catch {}
          }
        } catch {}

        // Fallback: локальный черновик
        let headers: any = { 'Content-Type': 'application/json' }
        if (!snapshotId) {
          try {
            const raw = localStorage.getItem(`tournament-budget-${(tournament as any)?.id}`)
            if (raw) {
              const parsed = JSON.parse(raw)
              if (parsed) {
                // Соберём объект для бэка: берем цифры из snapshot, добавляем byLevel для формулы
                const merged = { ...(parsed?.snapshot || {}), byLevel: parsed?.byLevel }
                headers['X-Budget-Data'] = JSON.stringify(merged)
              }
            }
          } catch {}
        } else if (snapshotPayload) {
          // Для снапшота также шлём snapshot + byLevel
          try {
            const merged = { ...(snapshotPayload?.snapshot || snapshotPayload || {}), byLevel: (snapshotPayload?.byLevel || snapshotPayload?.payload?.byLevel) }
            headers['X-Budget-Data'] = JSON.stringify(merged)
          } catch {}
          snapshotId = null
        }

        const url = new URL(`/api/reports/tournaments-profit/${(tournament as any)?.id}/budget-breakdown`, window.location.origin)
        if (snapshotId) url.searchParams.set('snapshotId', snapshotId)
        const res = await fetch(url.toString().replace(window.location.origin, ''), { headers })
        const data = await res.json()
        try {
          // Если сервер не прислал формулу мячей, восстановим по byLevel из budgetRaw
          const articles = Array.isArray(data?.articles) ? data.articles : []
          const incomeSection = articles.find((s: any) => s?.section === 'Доходы')
          const ballItem = incomeSection?.items?.find((it: any) => it?.name === 'Выручка от мячей')
          if (ballItem && !ballItem.budgetDetails) {
            const budgetRaw = data?.budgetRaw || data?.budget || {}
            const byLevel = budgetRaw?.byLevel
            if (byLevel && typeof byLevel === 'object') {
              const qtyByPrice: Record<string, number> = {}
              Object.keys(byLevel).forEach(lvl => {
                const p: any = byLevel[lvl] || {}
                const games = Number(p.games || 0)
                const ballsPerGame = Number(p.ballsPerGame || 0)
                const lostPercent = Number(p.lostPercent || 0)
                const firstPercent = Number(p.firstGradePercent || 0)
                const priceFirst = Number(p.priceFirstGrade || 0)
                const priceSecond = Number(p.priceSecondGrade || 0)
                if (games <= 0 || ballsPerGame <= 0) return
                const ballsUsed = games * ballsPerGame
                const sellable = Math.max(ballsUsed - ballsUsed * (lostPercent / 100), 0)
                const firstQty = sellable * (firstPercent / 100)
                const secondQty = sellable - firstQty
                if (priceFirst > 0 && firstQty > 0) qtyByPrice[priceFirst.toFixed(0)] = (qtyByPrice[priceFirst.toFixed(0)] || 0) + firstQty
                if (priceSecond > 0 && secondQty > 0) qtyByPrice[priceSecond.toFixed(0)] = (qtyByPrice[priceSecond.toFixed(0)] || 0) + secondQty
              })
              const entries = Object.entries(qtyByPrice).filter(([,q]) => (q as number) > 0.0001).sort((a,b)=> Number(b[0]) - Number(a[0]))
              if (entries.length > 0) {
                ballItem.budgetDetails = entries.map(([price, qty]) => `${Math.round(qty as number)}×${Number(price).toFixed(2).replace('.', ',')}`).join(' + ')
              }
            }
          }

          // Восстановим формулы бюджета по аренде, мячам, воде (как в «Расшифровке»)
          const expenseSection = articles.find((s: any) => s?.section === 'Расходы')
          const rentItem = expenseSection?.items?.find((it: any) => it?.name === 'Аренда кортов')
          const ballsCostItem = expenseSection?.items?.find((it: any) => it?.name === 'Себестоимость мячей')
          const waterCostItem = expenseSection?.items?.find((it: any) => it?.name === 'Себестоимость воды')
          const b = data?.budgetRaw || data?.budget || {}
          const byLevel2 = b?.byLevel
          const collectBlocks = (): any[] => {
            if (!byLevel2) return []
            // Вариант 1: объект { level: params }
            if (typeof byLevel2 === 'object' && !Array.isArray(byLevel2)) {
              const vals: any[] = []
              Object.values(byLevel2).forEach((v: any) => {
                if (!v) return
                if (Array.isArray(v)) vals.push(...v)
                else vals.push(v)
              })
              return vals
            }
            // Вариант 2: массив [{ level, ... }, { level, ... }]
            if (Array.isArray(byLevel2)) return byLevel2 as any[]
            return []
          }
          const blocks = collectBlocks()
          const sumByLevel = (getter: (p:any)=>number) => blocks.reduce((s:number, p:any)=> s + Number(getter(p)||0), 0)
          const hours = sumByLevel((p:any)=> Number(p.games||0) * Number(p.gameHours||0))
          const ballsQty = sumByLevel((p:any)=> Number(p.games||0) * Number(p.ballsPerGame||0))
          const waterQty = sumByLevel((p:any)=> Number(p.games||0) * Number(p.waterPerGame||0))
          const backendBudget = (data?.budget || {}) as any
          const rate = (() => {
            const r = Number(b?.courtRate || 0)
            if (r) return r
            const budgetRental = Number(backendBudget?.courtRental || 0)
            if (hours && budgetRental) return budgetRental / hours
            const snapRental = Number((b?.snapshot?.courtCost) || 0)
            if (hours && snapRental) return snapRental / hours
            return 0
          })()
          const ballsAvg = Number(b?.ballsAvg || 0)
          const waterAvg = Number(b?.waterAvg || 0)
          if (rentItem) {
            const snapCost = Number(b?.snapshot?.courtCost || 0)
            if (hours && rate) {
              rentItem.budgetDetails = `${hours.toFixed(1)}×${rate.toFixed(2).replace('.', ',')}`
              rentItem.budget = Number((hours * rate).toFixed(2))
            } else if (hours && snapCost) {
              const r = snapCost / hours
              rentItem.budgetDetails = `${hours.toFixed(1)}×${r.toFixed(2).replace('.', ',')}`
              rentItem.budget = Number(snapCost.toFixed(2))
            } else if (snapCost) {
              rentItem.budget = Number(snapCost.toFixed(2))
            }
          }
          if (ballsCostItem && !ballsCostItem.budgetDetails && ballsQty && ballsAvg) {
            ballsCostItem.budgetDetails = `${ballsQty.toFixed(0)}×${ballsAvg.toFixed(2).replace('.', ',')}`
          }
          if (ballsCostItem && (!Number(ballsCostItem.budget) || Number(ballsCostItem.budget) === 0) && ballsQty && ballsAvg) {
            ballsCostItem.budget = Number((ballsQty * ballsAvg).toFixed(2))
          }
          if (waterCostItem && !waterCostItem.budgetDetails && waterQty && waterAvg) {
            waterCostItem.budgetDetails = `${waterQty.toFixed(0)}×${waterAvg.toFixed(2).replace('.', ',')}`
          }
          if (waterCostItem && (!Number(waterCostItem.budget) || Number(waterCostItem.budget) === 0) && waterQty && waterAvg) {
            waterCostItem.budget = Number((waterQty * waterAvg).toFixed(2))
          }
        } catch {}
        setDetail(data)
      } finally { setLoading(false) }
    })()
  }, [tournament])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-30" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-lg border w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Бюджет (сравнение): {tournament.name}</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>

        <div className="p-4">
          {!loading && detail?.summary && (
            <div className="bg-gray-50 p-2 rounded text-xs mb-3">
              <div className="overflow-x-auto whitespace-nowrap text-gray-700 text-center">
                <span className="font-medium">Сводка по турниру:</span>
                {' '}Регистраций: <span className="font-medium">{detail.summary.totalMemberships || 0}</span>
                {' '}• Начислений: <span className="font-medium">{detail.summary.totalReceivables || 0}</span>
                {' '}• Отказов: <span className="font-medium">{detail.summary.totalCancellations || 0}</span>
                {' '}• Скидок: <span className="font-medium">{detail.summary.totalDiscounts || 0}</span>
              </div>
            </div>
          )}
          {loading && <div className="text-gray-500">Загрузка...</div>}
          {!loading && detail && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left px-3 py-2">Статья</th>
                    <th className="text-right px-3 py-2">Бюджет</th>
                    <th className="text-right px-3 py-2">Факт</th>
                    <th className="text-right px-3 py-2">Отклонение</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.articles?.map((section: any) => (
                    <React.Fragment key={section.section}>
                      <tr>
                        <td className="px-3 py-2 text-gray-700 font-medium" colSpan={4}>{section.section}</td>
                      </tr>
                      {section.items.map((item: any) => (
                        <tr key={item.name} className={item.isTotal ? 'font-semibold' : ''}>
                          <td className="px-3 py-2">{item.name}</td>
                          <td className="px-3 py-2 text-right align-top">
                            <div>{fmtMoney(item.budget)}</div>
                            {item.budgetDetails && <div className="text-xs text-gray-500 mt-1 whitespace-pre-line">{item.budgetDetails}</div>}
                          </td>
                          <td className="px-3 py-2 text-right align-top">
                            <div>{fmtMoney(item.actual)}</div>
                            {item.details && <div className="text-xs text-gray-500 mt-1 whitespace-pre-line">{item.details}</div>}
                          </td>
                          <td className={`px-3 py-2 text-right ${Number(item.deviation) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{Number(item.deviation) >= 0 ? '+' : ''}{fmtMoney(item.deviation)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t flex justify-end">
          <button className="px-3 py-2 text-sm rounded-md border text-gray-700 bg-white hover:bg-gray-50" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  )
}

export default TournamentBudgetComparisonModal


